/**
 * Global HTTP circuit breaker (LIL-331).
 *
 * Counts consecutive "backend looks down" failures (network errors, timeouts,
 * 5xx, 429) across every request. After {@link FAILURE_THRESHOLD} in a row it
 * trips OPEN for a cooldown — during which pollers pause instead of hammering a
 * service that's already struggling. When the cooldown elapses it goes
 * half-open (pollers resume); a single further failure re-trips immediately,
 * while any success closes it. Honors `Retry-After` for the cooldown length.
 *
 * State changes are broadcast as window events so UI (a banner) and pollers can
 * react, mirroring the existing `auth:unauthorized` event convention.
 */

const FAILURE_THRESHOLD = 5;
const DEFAULT_COOLDOWN_MS = 30_000;
const MAX_COOLDOWN_MS = 120_000;

export const CIRCUIT_OPEN_EVENT = 'circuit:open';
export const CIRCUIT_CLOSE_EVENT = 'circuit:close';

let consecutiveFailures = 0;
let openUntil = 0; // epoch ms; 0 = closed
let reopenTimer: ReturnType<typeof setTimeout> | null = null;

/** True while the breaker is tripped and requests should be held back. */
export function isCircuitOpen(): boolean {
  return openUntil > Date.now();
}

/** Ms remaining until the breaker goes half-open (0 if already closed). */
export function circuitCooldownRemaining(): number {
  return Math.max(0, openUntil - Date.now());
}

function emit(type: typeof CIRCUIT_OPEN_EVENT | typeof CIRCUIT_CLOSE_EVENT, detail?: unknown) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(type, { detail }));
}

function trip(cooldownMs: number) {
  const cooldown = Math.min(Math.max(cooldownMs, 0) || DEFAULT_COOLDOWN_MS, MAX_COOLDOWN_MS);
  openUntil = Date.now() + cooldown;
  if (reopenTimer !== null) clearTimeout(reopenTimer);
  reopenTimer = setTimeout(halfOpen, cooldown);
  emit(CIRCUIT_OPEN_EVENT, { cooldownMs: cooldown });
}

/**
 * Cooldown elapsed: let traffic flow again, but stay one failure away from
 * re-tripping so a still-dead backend doesn't get a fresh full threshold.
 */
function halfOpen() {
  reopenTimer = null;
  openUntil = 0;
  consecutiveFailures = FAILURE_THRESHOLD - 1;
  emit(CIRCUIT_CLOSE_EVENT);
}

/** Record a successful, backend-is-responsive response (any 2xx, or a <500 non-429 error). */
export function recordHttpSuccess() {
  consecutiveFailures = 0;
  if (openUntil !== 0) {
    openUntil = 0;
    if (reopenTimer !== null) {
      clearTimeout(reopenTimer);
      reopenTimer = null;
    }
    emit(CIRCUIT_CLOSE_EVENT);
  }
}

/** Record a "backend looks down" failure (network/timeout/5xx/429). */
export function recordHttpFailure(retryAfterMs?: number) {
  consecutiveFailures += 1;
  if (!isCircuitOpen() && consecutiveFailures >= FAILURE_THRESHOLD) {
    trip(retryAfterMs ?? DEFAULT_COOLDOWN_MS);
  }
}

/**
 * Parse an HTTP `Retry-After` header (delta-seconds or an HTTP-date) into ms.
 * Returns undefined when absent or unparseable.
 */
export function parseRetryAfter(headerValue: unknown): number | undefined {
  if (typeof headerValue !== 'string' || headerValue.trim() === '') return undefined;
  const seconds = Number(headerValue);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const dateMs = Date.parse(headerValue);
  if (Number.isNaN(dateMs)) return undefined;
  return Math.max(0, dateMs - Date.now());
}

/**
 * A {@link PauseSignal} adapter so backoff pollers pause while the breaker is
 * open and resume the moment it closes.
 */
export const circuitPauseSignal = {
  isPaused: isCircuitOpen,
  onChange(listener: () => void): () => void {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(CIRCUIT_OPEN_EVENT, listener);
    window.addEventListener(CIRCUIT_CLOSE_EVENT, listener);
    return () => {
      window.removeEventListener(CIRCUIT_OPEN_EVENT, listener);
      window.removeEventListener(CIRCUIT_CLOSE_EVENT, listener);
    };
  },
};

/** Test/HMR helper — reset all breaker state. */
export function resetCircuitBreaker() {
  consecutiveFailures = 0;
  openUntil = 0;
  if (reopenTimer !== null) {
    clearTimeout(reopenTimer);
    reopenTimer = null;
  }
}
